import type { ReactNode } from "react";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { PatientSuggestionResponse } from "../../../Types/VoiceAssistant/PatientSuggestionTypes";

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: "hidden" }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="primary.main"
        sx={{
          display: "block",
          px: 1.5,
          py: 0.75,
          bgcolor: "grey.50",
          textTransform: "uppercase",
          letterSpacing: 0.4,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ p: 1.25 }}>{children}</Box>
    </Paper>
  );
}

type Props = {
  suggestion: PatientSuggestionResponse;
};

export default function PatientSuggestionContent({ suggestion }: Props) {
  const patient = suggestion.data;
  const labServices = suggestion.services?.lab ?? [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        {suggestion.resultType ? (
          <Chip size="small" color="primary" label={suggestion.resultType} />
        ) : null}
        {patient?.patientId ? (
          <Chip size="small" variant="outlined" label={`EMR: ${patient.patientId}`} />
        ) : null}
        {patient?.encounterId != null ? (
          <Chip size="small" variant="outlined" label={`Encounter #${patient.encounterId}`} />
        ) : null}
        {patient?.patientMasterId != null ? (
          <Chip
            size="small"
            variant="outlined"
            label={`Patient master #${patient.patientMasterId}`}
          />
        ) : null}
      </Box>

      {(suggestion.diagnosis?.length ?? 0) > 0 ? (
        <SectionCard title="Diagnosis">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ICD code</TableCell>
                <TableCell>Diagnosis</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suggestion.diagnosis?.map((item) => (
                <TableRow key={`${item.icdCode}-${item.type}`}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{item.icdCode}</TableCell>
                  <TableCell>{item.icdName}</TableCell>
                  <TableCell>{item.type}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      ) : null}

      {(suggestion.suggestedDrugs?.length ?? 0) > 0 ? (
        <SectionCard title="Suggested drugs">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Keyword</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Quantity / dosage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suggestion.suggestedDrugs?.map((drug) => (
                <TableRow key={`${drug.keyword}-${drug.quantity}`}>
                  <TableCell sx={{ textTransform: "capitalize" }}>{drug.keyword}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{drug.medicineType}</TableCell>
                  <TableCell>{drug.quantity}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      ) : null}

      {(suggestion.prescription?.length ?? 0) > 0 ? (
        <SectionCard title="Recommended prescription">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Drug</TableCell>
                <TableCell>Generic name</TableCell>
                <TableCell>Strength</TableCell>
                <TableCell>Form</TableCell>
                <TableCell align="right">Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suggestion.prescription?.map((drug) => (
                <TableRow key={drug.drugId}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {drug.drugPackageName || "—"}
                    </Typography>
                    {drug.drugCode ? (
                      <Typography variant="caption" color="text.secondary">
                        {drug.drugCode}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{drug.drugGenericName || "—"}</TableCell>
                  <TableCell>{drug.drugStrength || "—"}</TableCell>
                  <TableCell>{drug.drugDosageForm || "—"}</TableCell>
                  <TableCell align="right">
                    {drug.drugPrice != null ? drug.drugPrice.toFixed(2) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      ) : null}

      {labServices.length > 0 ? (
        <SectionCard title="Suggested lab services">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>CPT code</TableCell>
                <TableCell>Service</TableCell>
                <TableCell align="right">Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {labServices.map((service) => (
                <TableRow key={`${service.cptId}-${service.cptCode}`}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{service.cptCode || "—"}</TableCell>
                  <TableCell>{service.cptName || service.cptDes || "—"}</TableCell>
                  <TableCell align="right">
                    {service.cptRate != null ? service.cptRate.toFixed(2) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      ) : null}
    </Box>
  );
}
